import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import {
  Badge, EmptyState, PageLoading, SearchInput, Select, TextInput,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type {
  DispatchSheet, DispatchStop, DispatchStopStatus, Order, TeamMember,
} from '../../types';

/**
 * Dispatch sheets — a day's delivery run.
 *
 * An admin builds a sheet from confirmed orders and hands it to a
 * delivery person. The stops are ordered nearest-first from the sheet's
 * start point so there is a sensible route to drive; the delivery app
 * links each stop out to Google Maps. Delivery captures a photo and a
 * signature; a failed drop records a reason.
 */

const money = (n: number | null) => (n == null ? '—' : `₹${n.toLocaleString('en-IN', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`);

const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'brand'> = {
  open: 'brand', completed: 'success', cancelled: 'neutral',
};
const STOP_TONE: Record<DispatchStopStatus, 'success' | 'warning' | 'neutral' | 'danger'> = {
  pending: 'warning', delivered: 'success', failed: 'danger',
};

const today = () => new Date().toISOString().slice(0, 10);

export default function DispatchSheetsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [sheets, setSheets] = useState<DispatchSheet[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | 'open' | 'completed' | 'cancelled'>('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ sheets: DispatchSheet[] }>('/dispatch/sheets', {
        status: statusFilter || undefined,
      });
      setSheets(data.sheets);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load dispatch sheets.');
      setSheets([]);
    }
  }, [toast, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <PageHeader
        title="Dispatch"
        subtitle="Build a day's delivery run from confirmed orders and hand it to a delivery person."
        actions={isAdmin ? (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} /> New dispatch sheet
          </button>
        ) : undefined}
      />

      <div className="page col gap-16">
        <div className="row gap-8" style={{ alignItems: 'center' }}>
          <Select
            label=""
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            style={{ maxWidth: 200 }}
          >
            <option value="">All sheets</option>
            <option value="open">Open</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </div>

        {sheets === null ? <PageLoading /> : sheets.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title="No dispatch sheets yet"
              body={isAdmin ? 'Create one from your confirmed orders.' : undefined}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Delivery person</th>
                    <th>Title</th>
                    <th className="num">Stops</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((s) => (
                    <tr key={s.id} className="clickable" onClick={() => setOpenId(s.id)}>
                      <td className="primary nowrap">{s.deliveryDate}</td>
                      <td>{s.deliveryUser?.name ?? '—'}</td>
                      <td>{s.title ?? <span className="faint">—</span>}</td>
                      <td className="num">{s.counts.total ?? 0}</td>
                      <td className="nowrap">
                        <span className="small">
                          {(s.counts.delivered ?? 0)} delivered
                          {(s.counts.failed ?? 0) > 0 ? `, ${s.counts.failed} failed` : ''}
                          {(s.counts.pending ?? 0) > 0 ? `, ${s.counts.pending} left` : ''}
                        </span>
                      </td>
                      <td><Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge></td>
                      <td>
                        <button type="button" className="icon-btn" title="Open"
                          onClick={(e) => { e.stopPropagation(); setOpenId(s.id); }}>
                          <Icon name="chevron-down" size={15} className="nav-caret" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {creating ? (
        <CreateSheetDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); void load(); setOpenId(id); }}
        />
      ) : null}

      {openId ? (
        <SheetDetailModal
          sheetId={openId}
          isAdmin={isAdmin}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function CreateSheetDialog({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (sheetId: string) => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [deliveryDate, setDeliveryDate] = useState(today());
  const [deliveryUserId, setDeliveryUserId] = useState('');
  const [title, setTitle] = useState('');
  const [startLat, setStartLat] = useState('');
  const [startLng, setStartLng] = useState('');
  const [showStart, setShowStart] = useState(false);

  const [drivers, setDrivers] = useState<TeamMember[]>([]);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get<TeamMember[]>('/users', { role: 'delivery', status: 'active' }),
      api.get<{ orders: Order[] }>('/orders', { status: 'confirmed', limit: 200 }),
    ])
      .then(([du, od]) => {
        if (cancelled) return;
        setDrivers(du);
        if (du[0]) setDeliveryUserId(du[0].id);
        setOrders(od.orders);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err instanceof ApiClientError ? err.message : 'Could not load.');
      });
    return () => { cancelled = true; };
  }, [toast]);

  const visibleOrders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (orders ?? []).filter((o) => !needle
      || o.orderNo.toLowerCase().includes(needle)
      || o.customer?.name?.toLowerCase().includes(needle)
      || o.customer?.route?.toLowerCase().includes(needle));
  }, [orders, search]);

  const toggle = (id: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allVisiblePicked = visibleOrders.length > 0 && visibleOrders.every((o) => picked.has(o.id));
  const toggleAll = () => setPicked((prev) => {
    const next = new Set(prev);
    if (allVisiblePicked) visibleOrders.forEach((o) => next.delete(o.id));
    else visibleOrders.forEach((o) => next.add(o.id));
    return next;
  });

  async function create() {
    if (!deliveryUserId || picked.size === 0) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        deliveryDate,
        deliveryUserId,
        orderIds: Array.from(picked),
        title: title.trim() || undefined,
      };
      if (showStart && startLat.trim() && startLng.trim()) {
        body.start = { latitude: Number(startLat), longitude: Number(startLng) };
      }
      const sheet = await api.post<DispatchSheet>('/dispatch/sheets', body);
      toast.success('Dispatch sheet created — the orders are now out for delivery.');
      onCreated(sheet.id);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not create the sheet.');
    } finally {
      setBusy(false);
    }
  }

  const noDrivers = drivers.length === 0;

  return (
    <Modal
      title="New dispatch sheet"
      subtitle="Pick a day, a delivery person, and the confirmed orders to send out."
      wide
      onClose={onClose}
      footer={(
        <>
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || noDrivers || !deliveryUserId || picked.size === 0}
            onClick={() => void create()}
          >
            {picked.size > 0 ? `Create & dispatch ${picked.size} order${picked.size > 1 ? 's' : ''}` : 'Create sheet'}
          </button>
        </>
      )}
    >
      <div className="col gap-12">
        {noDrivers ? (
          <div className="card card-pad" style={{ borderColor: 'var(--warning)', background: 'var(--warning-soft)' }}>
            <span className="row gap-8" style={{ alignItems: 'center' }}>
              <Icon name="alert" size={16} />
              <span className="small">
                No delivery person yet. Add someone with the Delivery role on the Team page first.
              </span>
            </span>
          </div>
        ) : null}

        <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
          <TextInput
            label="Delivery date" type="date" value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)} style={{ maxWidth: 180 }}
          />
          <Select
            label="Delivery person" value={deliveryUserId}
            onChange={(e) => setDeliveryUserId(e.target.value)} style={{ maxWidth: 220 }}
          >
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
          </Select>
          <TextInput
            label="Title (optional)" value={title} placeholder="Morning run"
            onChange={(e) => setTitle(e.target.value)} style={{ maxWidth: 220 }}
          />
        </div>

        <div className="col gap-4">
          <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
            onClick={() => setShowStart((v) => !v)}>
            <Icon name="pin" size={14} /> {showStart ? 'Hide start point' : 'Set a start point (optional)'}
          </button>
          {showStart ? (
            <div className="row gap-8" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <TextInput label="Start latitude" type="number" value={startLat} placeholder="19.07"
                onChange={(e) => setStartLat(e.target.value)} style={{ maxWidth: 160 }} />
              <TextInput label="Start longitude" type="number" value={startLng} placeholder="72.87"
                onChange={(e) => setStartLng(e.target.value)} style={{ maxWidth: 160 }} />
              <span className="small faint" style={{ paddingBottom: 8 }}>
                Stops are ordered nearest-first from here (e.g. your warehouse). Leave blank to
                start from the first stop.
              </span>
            </div>
          ) : null}
        </div>

        <div className="col gap-8">
          <div className="row gap-8" style={{ alignItems: 'center' }}>
            <strong className="small">Confirmed orders</strong>
            <Badge tone="neutral">{picked.size} selected</Badge>
            <span className="grow" />
            <SearchInput value={search} onChange={setSearch} placeholder="Order, customer or route" />
          </div>

          {orders === null ? <PageLoading rows={3} /> : orders.length === 0 ? (
            <div className="card">
              <EmptyState icon="list" title="No confirmed orders"
                body="Confirm orders on the Orders page before building a dispatch sheet." />
            </div>
          ) : (
            <div className="card" style={{ maxHeight: 340, overflow: 'auto' }}>
              <table className="data">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input type="checkbox" checked={allVisiblePicked} onChange={toggleAll}
                        aria-label="Select all shown" />
                    </th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Route</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((o) => (
                    <tr key={o.id} className="clickable" onClick={() => toggle(o.id)}>
                      <td>
                        <input type="checkbox" checked={picked.has(o.id)} onChange={() => toggle(o.id)}
                          onClick={(e) => e.stopPropagation()} aria-label={`Pick ${o.orderNo}`} />
                      </td>
                      <td className="primary nowrap">{o.orderNo}</td>
                      <td>{o.customer?.name ?? '—'}</td>
                      <td>{o.customer?.route ?? <span className="faint">—</span>}</td>
                      <td className="num nowrap">{money(o.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// =====================================================================

function mapsLink(stop: DispatchStop): string | null {
  const { latitude, longitude } = stop.customer;
  if (latitude == null || longitude == null) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
}

function SheetDetailModal({ sheetId, isAdmin, onClose, onChanged }: {
  sheetId: string;
  isAdmin: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [sheet, setSheet] = useState<DispatchSheet | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setSheet(await api.get<DispatchSheet>(`/dispatch/sheets/${sheetId}`));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not open the sheet.');
    }
  }, [sheetId, toast]);

  useEffect(() => { void load(); }, [load]);

  async function planRoute() {
    setBusy(true);
    try {
      await api.post(`/dispatch/sheets/${sheetId}/plan-route`);
      toast.success('Route re-ordered nearest-first.');
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not plan the route.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={sheet ? (sheet.title || `Run · ${sheet.deliveryDate}`) : 'Dispatch sheet'}
      subtitle={sheet
        ? `${sheet.deliveryUser?.name ?? 'Unassigned'} · ${sheet.deliveryDate}`
        : undefined}
      extraWide
      onClose={onClose}
      footer={(
        <>
          {isAdmin && sheet?.status === 'open' ? (
            <button type="button" className="btn btn-secondary" disabled={busy}
              onClick={() => void planRoute()}>
              <Icon name="map" size={15} /> Re-plan route
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </>
      )}
    >
      {sheet === null ? <PageLoading /> : (
        <div className="col gap-12">
          <div className="row gap-16" style={{ flexWrap: 'wrap' }}>
            <Stat label="Stops" value={String(sheet.counts.total ?? 0)} />
            <Stat label="Delivered" value={String(sheet.counts.delivered ?? 0)} />
            <Stat label="Failed" value={String(sheet.counts.failed ?? 0)} />
            <Stat label="Pending" value={String(sheet.counts.pending ?? 0)} />
            <Stat label="Status" value={sheet.status} />
          </div>

          {(sheet.stops ?? []).length === 0 ? (
            <EmptyState icon="list" title="No stops on this sheet" />
          ) : (
            <div className="card">
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th className="num">#</th>
                      <th>Customer</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Proof / reason</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(sheet.stops ?? []).map((stop) => {
                      const link = mapsLink(stop);
                      return (
                        <tr key={stop.id}>
                          <td className="num">{stop.sequence ?? '—'}</td>
                          <td className="primary">
                            {stop.customer.name ?? '—'}
                            {stop.customer.route
                              ? <div className="small faint">{stop.customer.route}</div> : null}
                          </td>
                          <td className="nowrap">
                            {stop.order.orderNo ?? '—'}
                            <div className="small faint">{money(stop.order.total)}</div>
                          </td>
                          <td><Badge tone={STOP_TONE[stop.status]}>{stop.status}</Badge></td>
                          <td>
                            {stop.status === 'delivered' ? (
                              <span className="row gap-8" style={{ alignItems: 'center' }}>
                                {stop.podPhotoUrl ? (
                                  <a href={stop.podPhotoUrl} target="_blank" rel="noreferrer">
                                    <img src={stop.podPhotoUrl} alt="Proof of delivery"
                                      style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                                  </a>
                                ) : null}
                                {stop.signatureUrl ? (
                                  <a href={stop.signatureUrl} target="_blank" rel="noreferrer">
                                    <img src={stop.signatureUrl} alt="Signature"
                                      style={{
                                        width: 60, height: 44, objectFit: 'contain',
                                        borderRadius: 6, background: '#fff', border: '1px solid var(--line)',
                                      }} />
                                  </a>
                                ) : null}
                              </span>
                            ) : stop.status === 'failed' ? (
                              <span className="small">
                                <strong>{stop.reason?.label ?? 'Not delivered'}</strong>
                                {stop.note ? <div className="faint">{stop.note}</div> : null}
                              </span>
                            ) : <span className="faint small">—</span>}
                          </td>
                          <td>
                            {link ? (
                              <a className="btn btn-ghost btn-sm" href={link} target="_blank" rel="noreferrer"
                                title="Open in Google Maps">
                                <Icon name="map" size={14} /> Maps
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="col" style={{ minWidth: 70 }}>
      <span className="small faint">{label}</span>
      <strong style={{ fontSize: 18, textTransform: 'capitalize' }}>{value}</strong>
    </div>
  );
}
